import type { TFunction } from 'i18next'
import type { DemandDriversData } from '../lib/demand-drivers'

import { format, parseISO } from 'date-fns'
import { RocketIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { summarizeDrivers } from '../lib/demand-drivers'
import { DemandDriversChart } from './DemandDriversChart'
import { InterpretationCard } from './InterpretationCard'

// Fallbacks for the existing `metric.*` keys so the UI stays readable
// before translations load; production copy comes from Crowdin.
const DRIVER_FALLBACK: Record<string, string> = {
	price_feature: 'Price',
	product_feature: 'Product features',
	support_feature: 'Promo',
	trend_feature: 'Trend',
	season_feature: 'Seasonality',
}

const driverLabel = (name: string, t: TFunction) =>
	t(`metric.${name}`, { defaultValue: DRIVER_FALLBACK[name] ?? name })

const formatPercent = (share: number) =>
	formatNumber(share * 100, NumberFormats.Percent)

const formatChange = (pp: number, t: TFunction) =>
	t('midterm_forecast.drivers.change_pp', {
		defaultValue: '{value} pp',
		value: `${pp > 0 ? '+' : ''}${formatNumber(pp, NumberFormats.Standart)}`,
	})

const changeTone = (pp: number) => {
	if (pp > 0) return 'text-success'
	if (pp < 0) return 'text-destructive'

	return 'text-muted-foreground'
}

export const DemandDriversCard = ({
	data,
	isPending,
	isError,
}: {
	data: DemandDriversData | undefined
	isPending: boolean
	isError: boolean
}) => {
	const { t } = useTranslation()

	if (isPending) {
		return (
			<Skeleton className='h-16 w-full' data-testid='mtf-drivers-skeleton' />
		)
	}

	if (isError) {
		return (
			<p
				className='rounded-lg border p-3 text-sm text-muted-foreground'
				data-testid='mtf-drivers-error'
			>
				{t('midterm_forecast.drivers.error', {
					defaultValue: 'Failed to load demand drivers.',
				})}
			</p>
		)
	}

	const summary = summarizeDrivers(data)

	if (!data?.rows.length || !summary.topDriver) {
		return (
			<p
				className='rounded-lg border p-3 text-sm text-muted-foreground'
				data-testid='mtf-drivers-empty'
			>
				{t('midterm_forecast.drivers.empty', {
					defaultValue: 'No driver data for this period yet.',
				})}
			</p>
		)
	}

	const labels = Object.fromEntries(
		data.drivers.map((name) => [name, driverLabel(name, t)])
	)
	const topLabel = labels[summary.topDriver]
	const latestDate = parseISO(data.rows[data.rows.length - 1].date)

	return (
		<InterpretationCard
			icon={<RocketIcon className='size-4 text-primary' />}
			title={t('midterm_forecast.drivers.title', {
				defaultValue: 'Demand drivers',
			})}
			badge={
				<Badge variant='neutral' data-testid='mtf-drivers-badge'>
					{topLabel}
				</Badge>
			}
			toggleTestId='mtf-drivers-toggle'
			data-testid='mtf-drivers-card'
		>
			<div>
				<p className='mb-1 text-xs font-bold tracking-wide text-muted-foreground uppercase'>
					{t('midterm_forecast.drivers.caption', {
						defaultValue: 'Driver share · {month} to date',
						month: format(latestDate, 'LLLL'),
					})}
				</p>
				<div className='rounded-lg border p-2.5'>
					<DemandDriversChart
						rows={data.rows}
						drivers={data.drivers}
						labels={labels}
						data-testid='mtf-drivers-chart'
					/>
				</div>
			</div>
			<div className='grid grid-cols-2 gap-2'>
				<div
					className='rounded-lg bg-secondary/50 p-2.5'
					data-testid='mtf-drivers-top'
				>
					<p className='text-xs text-muted-foreground'>
						{t('midterm_forecast.drivers.tile_top', {
							defaultValue: 'Top driver',
						})}
					</p>
					<p className='text-sm font-bold'>{topLabel}</p>
					<p className='text-xs text-muted-foreground tabular-nums'>
						{formatPercent(summary.latestShare ?? 0)}
					</p>
				</div>
				<div
					className='rounded-lg bg-secondary/50 p-2.5'
					data-testid='mtf-drivers-change'
				>
					<p className='text-xs text-muted-foreground'>
						{t('midterm_forecast.drivers.tile_change', {
							defaultValue: '10 days change',
						})}
					</p>
					{summary.change10d === null || !summary.compareDate ? (
						<>
							<p className='text-sm font-bold text-muted-foreground'>—</p>
							<p className='text-xs text-muted-foreground'>
								{t('midterm_forecast.drivers.not_enough_days', {
									defaultValue: 'Not enough days yet',
								})}
							</p>
						</>
					) : (
						<>
							<p
								className={cn(
									'text-sm font-bold tabular-nums',
									changeTone(summary.change10d)
								)}
							>
								{formatChange(summary.change10d, t)}
							</p>
							<p className='text-xs text-muted-foreground'>
								{t('midterm_forecast.drivers.vs_date', {
									defaultValue: 'vs {date}',
									date: format(parseISO(summary.compareDate), 'd MMM'),
								})}
							</p>
						</>
					)}
				</div>
			</div>
		</InterpretationCard>
	)
}
